/**
 * @friskai/frisk-js Session Registry
 *
 * Global registry for tracking Frisk sessions.
 */

import { SessionNotFoundError } from '../errors'
import type { FriskSession } from './frisk-session'

/**
 * Global registry for Frisk sessions
 *
 * This singleton registry maintains a mapping of session IDs to FriskSession instances.
 * It allows callbacks and middleware to retrieve session context without direct references.
 */
export class SessionRegistry {
  private static instance: SessionRegistry | null = null
  private sessions: Map<string, FriskSession> = new Map()

  /**
   * Get the singleton SessionRegistry instance
   */
  static getInstance(): SessionRegistry {
    if (!SessionRegistry.instance) {
      SessionRegistry.instance = new SessionRegistry()
    }
    return SessionRegistry.instance
  }

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {}

  /**
   * Register a session in the registry
   */
  register(sessionId: string, session: FriskSession): void {
    this.sessions.set(sessionId, session)
  }

  /**
   * Get a session by ID
   * @throws {SessionNotFoundError} if session is not found
   */
  get(sessionId: string): FriskSession {
    const session = this.sessions.get(sessionId)
    if (!session) {
      throw new SessionNotFoundError(sessionId)
    }
    return session
  }

  /**
   * Check if a session exists
   */
  has(sessionId: string): boolean {
    return this.sessions.has(sessionId)
  }

  /**
   * Remove a session from the registry
   */
  unregister(sessionId: string): boolean {
    return this.sessions.delete(sessionId)
  }

  /**
   * Clear all sessions (useful for testing)
   */
  clear(): void {
    this.sessions.clear()
  }

  /**
   * Get the number of registered sessions
   */
  get size(): number {
    return this.sessions.size
  }
}
